import axios from 'axios';
import FileDownload from 'js-file-download';
import request, { apiHost, config } from '../../utils/request';

// Helper function to build query string
const buildQueryString = params => {
  const filtered = Object.entries(params).reduce((acc, [key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      acc[key] = value;
    }
    return acc;
  }, {});
  return new URLSearchParams(filtered).toString();
};

// Fetches a report's Excel export as a binary buffer and triggers a browser download.
export const exportReportExcel = async (path, params, fileName) => {
  const queryString = buildQueryString(params);
  const url = queryString ? `${apiHost}${path}?${queryString}` : `${apiHost}${path}`;
  const response = await axios.get(url, { ...config(), responseType: 'arraybuffer' });
  FileDownload(response.data, fileName);
};

// API Service for Black Friday Reports
// Uses the existing request utility which automatically includes auth tokens
export const reportsAPI = {
  /**
   * Get PDF Activity Report
   */
  async getPdfActivity(params = {}) {
    const queryString = buildQueryString(params);
    const url = queryString
      ? `/reports/pdf-activity?${queryString}`
      : `/reports/pdf-activity`;
    const response = await request.get(url);
    return response.data;
  },

  /**
   * Get Email Activity Report
   */
  async getEmailActivity(params = {}) {
    const queryString = buildQueryString(params);
    const url = queryString
      ? `/reports/email-activity?${queryString}`
      : `/reports/email-activity`;
    const response = await request.get(url);
    return response.data;
  },

  /**
   * Get Order Activity Report
   */
  async getOrderActivity(params = {}) {
    const queryString = buildQueryString(params);
    const url = queryString
      ? `/reports/order-activity?${queryString}`
      : `/reports/order-activity`;
    const response = await request.get(url);
    return response.data;
  },

  /**
   * Get Combined Activity Report
   */
  async getCombinedActivity(params = {}) {
    const queryString = buildQueryString(params);
    const url = queryString
      ? `/reports/combined-activity?${queryString}`
      : `/reports/combined-activity`;
    const response = await request.get(url);
    return response.data;
  },

  /**
   * List active channels, for the Channel filter dropdown.
   */
  async getChannels() {
    const response = await request.get('/channels');
    return response.data;
  },

  /**
   * Get Black Friday Report
   * @param {string} [date] - YYYY-MM-DD, defaults to the current year's Black Friday
   * @param {Object} [options] - { daysBefore, daysAfter } for the daily trend window (default 7/7)
   */
  async getBlackFridayReport(date = null, options = {}) {
    const queryString = buildQueryString({ date, ...options });
    const url = queryString ? `/reports/black-friday?${queryString}` : `/reports/black-friday`;
    const response = await request.get(url);
    return response.data;
  },

  /**
   * Black Friday PDF export (async job pipeline).
   * Enqueue a REPORT_PDF job — returns { jobId, status: 'PREPARING' }.
   */
  async requestBlackFridayPdf(date = null) {
    const queryString = date ? `?date=${date}` : '';
    const url = `/reports/black-friday/export/pdf${queryString}`;
    const response = await request.get(url);
    return response.data;
  },

  /**
   * Poll job status — PREPARING | AVAILABLE | FAILED.
   */
  async getBlackFridayPdfStatus(jobId) {
    const response = await request.get(`/reports/black-friday/export/pdf/${jobId}/status`);
    return response.data;
  },

  /**
   * Get Date Range Comparison Report
   */
  async getDateRangeComparison(params = {}) {
    const queryString = buildQueryString(params);
    const url = `/reports/date-range-comparison?${queryString}`;
    const response = await request.get(url);
    return response.data;
  },

  /**
   * Get API Requests Report
   */
  async getApiRequests(params = {}) {
    const queryString = buildQueryString(params);
    const url = queryString
      ? `/reports/api-requests?${queryString}`
      : `/reports/api-requests`;
    const response = await request.get(url);
    return response.data;
  },

  /**
   * Whether the current user's reports data is role-scoped — the authoritative source for
   * this is the backend's actual authorization decision (see getOrderReportScope), not a
   * frontend role-name list that can drift from it.
   */
  async getMyReportsAccess() {
    const response = await request.get('/reports/my-access');
    return response.data;
  },

  /**
   * Phase 7 drill-down: record-level rows behind a chart segment, paginated.
   */
  async getOrderActivityRecords(params = {}) {
    const response = await request.get(`/reports/order-activity/records?${buildQueryString(params)}`);
    return response.data;
  },

  async getPdfActivityRecords(params = {}) {
    const response = await request.get(`/reports/pdf-activity/records?${buildQueryString(params)}`);
    return response.data;
  },

  async getEmailActivityRecords(params = {}) {
    const response = await request.get(`/reports/email-activity/records?${buildQueryString(params)}`);
    return response.data;
  },

  async getApiRequestRecords(params = {}) {
    const response = await request.get(`/reports/api-requests/records?${buildQueryString(params)}`);
    return response.data;
  },

  /**
   * Phase 9: export audit trail — who downloaded which report, when.
   */
  async getExportAuditTrail(params = {}) {
    const response = await request.get(`/reports/export-audit?${buildQueryString(params)}`);
    return response.data;
  },

  /**
   * Phase 11: rolling multi-week orders/pdfs/emails trend.
   */
  async getMultiPeriodTrend(params = {}) {
    const response = await request.get(`/reports/multi-period-trend?${buildQueryString(params)}`);
    return response.data;
  }
};

export default reportsAPI;
